import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { BRAND, STATUS, INK, FONT_MONO } from 'Utils/styleTokens';
import { buildCensus, sourceStage } from 'Utils/sourceCensus';
import { riskTier } from 'Utils/riskTier';
import AppContext from 'Utils/AppContext';
import { startDiscoveryWithRule } from 'DataDiscovery/discoverApi';
import { useCanEdit, READ_ONLY_TITLE } from 'Auth/permissions';
import { listProfileJobs } from 'Metadata/profileApi';
import { getDataSourceNumericId, runDataSourceTest } from 'DataSources/datasourcesApi';
import { ensureConnectionTested } from 'Utils/connectionGate';
import AdoptedFromBadge from 'DataSources/AdoptedFromBadge';
import { getLandscape } from 'AssetManager/manage-api';
import { listDrivers } from 'Drivers/api';
import { engineHasDriver } from 'Utils/driverUtils';
import {
  kindForSource,
  inferCloud,
  isInternalExposure,
  isLoudExposure,
  rangeProvenanceLabel,
  sortPortExposures,
  getHideInternalPref,
} from 'AssetManager/LandscapeUtils';
import {
  isResourceFullyConnected,
  ipDnsForResource,
  managerSourceToEditorPrefill,
  encodePrefill,
} from 'SensitiveDataLandscape/UnknownSources/managerSources';

// Stage palette from the mockups: Surfaced light-grey (pre-connection or not yet verified),
// Scanned cyan, Discovered blue, Masked violet. There is no Connected column: saving a data
// source auto-runs a connection test and profile on the backend, so a source either passes
// (→ Profiled) or stays Surfaced with its failure visible. Each column's action button is
// filled with the NEXT stage's color; Surfaced uses a neutral dark button since its actions
// onboard/repair from outside the pipeline rather than advancing a stage.
const STAGES = [
  { key: 'surfaced', title: 'Surfaced', dot: INK[300], action: 'Connect', actionColor: INK[700] },
  { key: 'scanned', title: 'Fingerprinted', dot: BRAND.cyan, action: 'Run discovery', actionColor: BRAND.blue },
  { key: 'discovered', title: 'Discovered', dot: BRAND.blue, action: 'Mask now', actionColor: BRAND.violet },
  { key: 'masked', title: 'Secured', dot: BRAND.violet, action: 'Re-run mask', actionColor: BRAND.violet },
];

// Port-exposure match → chip color. c2-manager classifies each open port against its
// engine→default-port table: EXACT = a standard DB port (e.g. 5432/3306), NEARBY = within
// its default-port window, NON_STANDARD = an open port on no known DB port.
const PORT_MATCH_META = {
  exact: { label: 'exact', bg: STATUS.success.bg, text: STATUS.success.text },
  nearby: { label: 'nearby', bg: STATUS.warning.bg, text: STATUS.warning.text },
  non_standard: { label: 'non-standard', bg: STATUS.neutral.bg, text: STATUS.neutral.text },
};

// The port(s) to show on a surfaced card, ordered most-likely-a-database first (exact →
// nearby → non_standard). Prefer the manager's classified `portExposures` (each carries
// port/portEnd range, the matched engine, and the match kind); fall back to the single
// top-level `port` when no exposures were reported.
const portChipsFor = (resource = {}) => {
  const exposures = Array.isArray(resource.portExposures) ? resource.portExposures : [];
  if (exposures.length) {
    return sortPortExposures(exposures).map((e) => ({
      label: e.portEnd != null && e.portEnd !== e.port ? `${e.port}–${e.portEnd}` : String(e.port),
      match: String(e.match || '').toLowerCase(),
      engine: e.knownDatabase || '',
      sourceRange: e.sourceRange || '',
      // Reachability + provenance carried through so the chip color can distinguish an
      // internal, range-swept port from a port a rule opened to an external source.
      internal: isInternalExposure(e),
      loud: isLoudExposure(e),
      internalNetwork: e.internalNetwork || '',
      provenance: rangeProvenanceLabel(e),
    }));
  }
  if (resource.port != null) {
    return [{ label: String(resource.port), match: '', engine: resource.engine || '', sourceRange: '', internal: false, loud: false, internalNetwork: '', provenance: null }];
  }
  return [];
};

// How long an optimistically-started source stays "Running…" before deferring to the polled
// job state. Longer than the 30s landscape poll so at least one refresh confirms the job first.
const STARTED_TTL_MS = 40000;

// Column keys of this board for the shared stage vocabulary (Utils/sourceCensus.sourceStage).
const COLUMN_OF_STAGE = { secured: 'masked', discovered: 'discovered', profiled: 'scanned', surfaced: 'surfaced' };

/**
 * Which pipeline column a source belongs in. The rule itself lives in Utils/sourceCensus
 * (sourceStage) so the Workflow header funnel and this board can never disagree; this maps its
 * vocabulary onto the board's column keys.
 */
const stageOf = (source, ctx) => COLUMN_OF_STAGE[sourceStage(source, ctx)];

function PipelineCard({ source, stage, maskStatus, onAction, busy, running, locked, handoff, profiled, provenance, subCount = 0, readOnlyUser = false }) {
  const tier = riskTier(source.latestRiskLevel);
  const pill = { label: tier.label, color: tier.text, bg: tier.bg };
  const hasError = maskStatus === 'ERROR';
  // A live job (from the job lists) OR the just-clicked optimistic state both lock the button.
  const isBusy = busy || running;
  // A prod source can't be masked in place, so its Discovered card hands off to the Enterprise
  // Data Landscape findings ledger instead of offering a dead button. The hand-off outranks the
  // busy/running labels: it only navigates, so it stays clickable while a job is in flight.
  const buttonLabel = locked
    ? 'READ ONLY'
    : handoff
      ? 'Generate list →'
      : busy
        ? 'Starting…'
        : running
          ? 'Running…'
          : `${stage.action} →`;
  const statLine =
    stage.key === 'masked'
      ? 'masking up to date'
      : stage.key === 'discovered'
        ? hasError
          ? 'mask job failed'
          : `risk score ${source.latestRiskScore != null ? source.latestRiskScore : '—'}`
        : profiled
          ? 'fingerprinted, not discovered'
          : 'connection verified';
  return (
    <div
      className='sg-card mb-2'
      style={{ padding: '10px 12px' }}
      data-testid={`pipeline-card-${source.id}`}
    >
      <div className='d-flex justify-content-between align-items-center'>
        <Link
          to={`/landscape/riskprofile/${encodeURIComponent(source.id)}`}
          style={{ fontFamily: FONT_MONO, fontSize: 12.5, color: 'var(--ink-900)', fontWeight: 600 }}
        >
          {String(source.name || '').slice(0, 30)}
          <AdoptedFromBadge
            registeredServiceId={provenance?.registeredServiceId}
            registeredServiceName={provenance?.registeredServiceName}
          />
        </Link>
        {hasError && (
          <span className='sg-pill sg-pill--danger' style={{ fontSize: 11 }}>
            ERROR
          </span>
        )}
      </div>
      <div className='sg-small' style={{ color: 'var(--ink-500)', margin: '3px 0' }}>
        {source.engine || source.datasourceType}
        {source.database ? ` · ${source.database}` : ''}
        {subCount > 0 && (
          <Link
            to={`/landscape/riskprofile/${encodeURIComponent(source.id)}`}
            title='Sub data sources spawned from this source. They are listed on its risk profile.'
            style={{ marginLeft: 6, fontSize: 11.5, color: 'var(--ink-500)', textDecoration: 'underline' }}
          >
            {subCount} sub-source{subCount > 1 ? 's' : ''}
          </Link>
        )}
      </div>
      <div className='d-flex align-items-center' style={{ gap: 8, marginBottom: 8 }}>
        <span className='sg-pill sg-pill--nodot' style={{ background: pill.bg, color: pill.color, fontSize: 11.5 }}>
          {pill.label}
        </span>
        <span className='sg-small' style={{ color: 'var(--ink-500)' }}>
          {statLine}
        </span>
      </div>
      <button
        type='button'
        className='w-100'
        disabled={(isBusy && !handoff) || locked || readOnlyUser}
        onClick={() => onAction(source, stage)}
        title={
          readOnlyUser
            ? READ_ONLY_TITLE
            : locked
            ? 'This data source is read only.'
            : handoff
              ? 'This is a prod source. Review its findings on the Enterprise Data Landscape.'
              : undefined
        }
        data-testid={`pipeline-action-${source.id}`}
        style={{
          background: locked ? INK[300] : running && !handoff ? INK[400] : stage.actionColor,
          color: '#fff',
          border: 'none',
          borderRadius: 'var(--r-sm)',
          padding: '6px 10px',
          fontSize: 12,
          fontWeight: 600,
          opacity: (isBusy && !handoff) || locked || readOnlyUser ? 0.6 : 1,
          cursor: (isBusy && !handoff) || locked || readOnlyUser ? 'default' : 'pointer',
        }}
      >
        {buttonLabel}
      </button>
    </div>
  );
}

// Show at most this many port chips/rows collapsed; the rest sit behind "+N more".
const PORTS_COLLAPSED_COUNT = 2;

/**
 * Card for a cloud-landscape resource that is NOT yet (fully) a data source. Shows the
 * resource name, cloud/source/engine, endpoint, and its open ports (exact matches first,
 * first two visible, rest expandable). "Connect" opens the Source Editor pre-filled from
 * the resource — or the per-port table page when more than one port is exposed. When none
 * of the implied engines has an installed driver the button becomes "Upload driver".
 */
function SurfacedCard({ resource, onCreate, hideInternal, hasDriver, onUploadDriver }) {
  const name = resource.name || resource.database || resource.identifier || '(unnamed)';
  const host = ipDnsForResource(resource);
  const [expanded, setExpanded] = useState(false);
  const allPorts = portChipsFor(resource);
  // "Hide internal-network sources" pref filters the chips client-side (full payload always kept).
  const ports = hideInternal ? allPorts.filter((p) => !p.internal) : allPorts;
  const visiblePorts = expanded ? ports : ports.slice(0, PORTS_COLLAPSED_COUNT);
  const hiddenCount = ports.length - visiblePorts.length;
  const subtitle = [resource.cloud || inferCloud(resource.source), resource.source, resource.engine]
    .filter(Boolean)
    .join(' · ');
  return (
    <div
      className='sg-card mb-2'
      style={{ padding: '10px 12px' }}
      data-testid={`surfaced-card-${resource.identifier || name}`}
    >
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: 12.5,
          color: 'var(--ink-900)',
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={String(name)}
      >
        {String(name).slice(0, 30)}
      </div>
      <div className='sg-small' style={{ color: 'var(--ink-500)', margin: '3px 0' }}>
        {subtitle}
      </div>
      {host && (
        <div
          className='sg-small'
          style={{ color: 'var(--ink-500)', fontFamily: FONT_MONO, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={host}
        >
          {host}
        </div>
      )}
      <div className='d-flex flex-wrap align-items-center' style={{ gap: 4, margin: '6px 0 8px' }}>
        {ports.length === 0 ? (
          <span className='sg-small' style={{ color: 'var(--ink-500)' }}>no open port</span>
        ) : (
          visiblePorts.map((p, i) => {
            const meta = PORT_MATCH_META[p.match] || { label: p.match, bg: STATUS.neutral.bg, text: STATUS.neutral.text };
            // Reachability still drives the chip color (internal muted, deliberately-external
            // loud red) even though the words moved to the tooltip — the visible text is just
            // the port and the allowing IP range.
            const style = p.internal
              ? { background: STATUS.neutral.bg, color: STATUS.neutral.text, fontSize: 11 }
              : p.loud
                ? { background: STATUS.danger.bg, color: STATUS.danger.text, fontSize: 11 }
                : { background: meta.bg, color: meta.text, fontSize: 11 };
            const reach = p.internal
              ? `internal${p.internalNetwork ? ` (${p.internalNetwork})` : ''}`
              : p.loud
                ? 'external'
                : '';
            const title = [
              p.engine || 'port',
              p.match ? meta.label : null,
              reach || null,
              p.provenance || null,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <span
                key={`${p.label}-${i}`}
                className='sg-pill sg-pill--nodot'
                style={style}
                title={title}
              >
                {p.label}
                {p.sourceRange ? ` · ${p.sourceRange}` : ''}
              </span>
            );
          })
        )}
        {hiddenCount > 0 && (
          <button
            type='button'
            className='sg-pill sg-pill--nodot'
            onClick={() => setExpanded(true)}
            data-testid={`surfaced-more-ports-${resource.identifier || name}`}
            style={{ background: STATUS.neutral.bg, color: STATUS.neutral.text, fontSize: 11, border: 'none', cursor: 'pointer' }}
          >
            +{hiddenCount} more
          </button>
        )}
        {expanded && ports.length > PORTS_COLLAPSED_COUNT && (
          <button
            type='button'
            className='sg-pill sg-pill--nodot'
            onClick={() => setExpanded(false)}
            style={{ background: STATUS.neutral.bg, color: STATUS.neutral.text, fontSize: 11, border: 'none', cursor: 'pointer' }}
          >
            show less
          </button>
        )}
      </div>
      <button
        type='button'
        className='w-100'
        onClick={() => (hasDriver ? onCreate(resource) : onUploadDriver())}
        data-testid={`surfaced-action-${resource.identifier || name}`}
        title={hasDriver ? undefined : 'No JDBC driver is installed for this engine yet.'}
        style={{
          background: INK[700],
          color: '#fff',
          border: 'none',
          borderRadius: 'var(--r-sm)',
          padding: '6px 10px',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        {hasDriver ? 'Connect →' : 'Upload driver →'}
      </button>
    </div>
  );
}

/**
 * Card for a SAVED data source whose connection test failed or never ran — with the
 * Connected column gone, these stay in Surfaced until a test passes (the backend re-tests
 * automatically on every save). Primary action re-runs the test in place; Edit opens the
 * Source Editor to fix credentials.
 */
function SavedSourceCard({ source, onRetried, notify }) {
  const [busy, setBusy] = useState(false);
  const canEdit = useCanEdit();
  const failed = source.lastTestSuccess === false;
  const retry = async () => {
    setBusy(true);
    try {
      const id = getDataSourceNumericId({ id: source.id, tableData: { id: source.id } }) ?? Number(source.id);
      const result = await runDataSourceTest(id);
      const ok = result && (result.success === true || result.lastTestSuccess === true);
      notify(
        ok ? 'Connection verified' : 'Connection test failed',
        ok
          ? `${source.name} connected successfully.`
          : `${source.name} still cannot connect. Edit the source to fix its settings.`
      );
      if (onRetried) await onRetried();
    } catch (e) {
      console.log(`Retry test failed for ${source.name}`, e);
      notify(`Could not test ${source.name}`, String(e.message || e));
    }
    setBusy(false);
  };
  return (
    <div
      className='sg-card mb-2'
      style={{ padding: '10px 12px' }}
      data-testid={`pipeline-card-${source.id}`}
    >
      <div className='d-flex justify-content-between align-items-center'>
        <Link
          to={`/landscape/riskprofile/${encodeURIComponent(source.id)}`}
          style={{ fontFamily: FONT_MONO, fontSize: 12.5, color: 'var(--ink-900)', fontWeight: 600 }}
        >
          {String(source.name || '').slice(0, 30)}
        </Link>
        <Link to={`/sources/source-editor/${encodeURIComponent(source.id)}`} className='sg-small'>
          Edit
        </Link>
      </div>
      <div className='sg-small' style={{ color: 'var(--ink-500)', margin: '3px 0' }}>
        {source.engine || source.datasourceType}
        {source.database ? ` · ${source.database}` : ''}
      </div>
      <div className='sg-small' style={{ color: failed ? STATUS.danger.text : 'var(--ink-500)', marginBottom: 8 }}>
        {failed ? 'connection test failed' : 'not yet tested'}
      </div>
      <button
        type='button'
        className='w-100'
        disabled={busy || !canEdit}
        title={!canEdit ? READ_ONLY_TITLE : undefined}
        onClick={retry}
        data-testid={`pipeline-action-${source.id}`}
        style={{
          background: INK[700],
          color: '#fff',
          border: 'none',
          borderRadius: 'var(--r-sm)',
          padding: '6px 10px',
          fontSize: 12,
          fontWeight: 600,
          opacity: busy ? 0.6 : 1,
          cursor: busy ? 'default' : 'pointer',
        }}
      >
        {busy ? 'Testing…' : 'Retry test →'}
      </button>
    </div>
  );
}

/**
 * Pipeline board — mockup 2a. Places every source from risk-overview into
 * Surfaced → Profiled → Discovered → Secured by derived status:
 * masked = whole-source maskStatus from the entity roll-up; discovered = has a
 * latest discovery; scanned = has a profile (meta) job or a passing connection
 * test; the rest (untested / failing) stay Surfaced next to the cloud-landscape
 * candidates. Saving a source auto-runs test + profile on the backend, so healthy
 * sources flow through Surfaced without manual action.
 */
function PipelineBoard({ sources, maskStatusById, runningById }) {
  const history = useHistory();
  const { setNotification, connections, loadConnectionsHandler } = useContext(AppContext) || {};
  const canEdit = useCanEdit();
  // The risk-overview DTO carries no readOnly flag — join it from the globally loaded
  // connections. Prod (read-only, source-of-record) rows can't be masked in place: the
  // one-click "Mask now" is in-place, and in-place masking mutates the source. (Meta scan
  // and discovery only read, so those actions stay enabled.) In the Discovered column they
  // get the EDL findings hand-off instead; in Secured the button stays locked. Cross-source
  // masking of a prod source is still available via New Masking Job → target selection.
  const prodIds = new Set(
    (connections || []).filter((c) => c && c.readOnly === true).map((c) => String(c.id))
  );
  // Sub-sources (parentDataSourceId set) never get their own board card — the census below
  // drops them; the parent card carries an "N sub-sources" link to its risk profile instead.
  const subCountByParent = useMemo(() => {
    const map = new Map();
    (connections || []).forEach((c) => {
      if (c && c.parentDataSourceId != null) {
        const key = String(c.parentDataSourceId);
        map.set(key, (map.get(key) || 0) + 1);
      }
    });
    return map;
  }, [connections]);
  const [profiledIds, setProfiledIds] = useState(new Set());
  // Raw cloud-landscape resources (c2-manager /landscape). Fetched once; the "not yet
  // connected" subset is recomputed against `connections` so a just-created connection
  // drops out of the Surfaced column without a refetch.
  const [landscapeSources, setLandscapeSources] = useState([]);
  // Installed JDBC drivers — drives the Surfaced card's Connect vs Upload-driver button.
  const [drivers, setDrivers] = useState([]);
  const [busyId, setBusyId] = useState(null);
  // Sources we just kicked off a job for → id: timestamp. Bridges the gap between the start
  // call returning and the next 30s poll reflecting the job as RUNNING in the job lists.
  // After STARTED_TTL_MS we defer entirely to the polled `runningById`, so a finished job
  // correctly reverts the button to its action label.
  const [startedAt, setStartedAt] = useState({});
  const [query, setQuery] = useState('');
  // Shared "Hide internal-network sources" view pref (toggled from the Resource Manager table).
  // Read once on mount — client-side chip filtering, no re-scan.
  const [hideInternal] = useState(getHideInternalPref);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const page = await listProfileJobs({ page: 0, size: 500 });
        const jobs = Array.isArray(page?.content) ? page.content : Array.isArray(page) ? page : [];
        if (!cancelled) {
          setProfiledIds(new Set(jobs.map((j) => Number(j.dataSourceId)).filter((n) => Number.isFinite(n))));
        }
      } catch (e) {
        console.log('listProfileJobs for pipeline board failed', e);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Cloud landscape for the Surfaced column: databases/servers c2-manager can see but that
  // aren't data sources yet. Fetched once — the dedup against `connections` happens in the memo.
  useEffect(() => {
    let cancelled = false;
    getLandscape()
      .then((res) => {
        if (!cancelled) setLandscapeSources(Array.isArray(res?.sources) ? res.sources : []);
      })
      .catch((e) => console.log('getLandscape for pipeline Surfaced column failed', e));
    listDrivers()
      .then((list) => {
        if (!cancelled) setDrivers(Array.isArray(list) ? list : []);
      })
      .catch((e) => console.log('listDrivers for pipeline Surfaced column failed', e));
    return () => {
      cancelled = true;
    };
  }, []);

  // Databases + servers (not object storage) that expose a port and aren't fully covered by
  // saved connections yet. Coverage is per-port: a resource with two exposed DB ports keeps
  // its card until BOTH ports have a data source. Recomputes when connections change so
  // creating one updates the column without a refetch.
  const surfaced = useMemo(() => {
    return (landscapeSources || []).filter((r) => {
      const kind = kindForSource(r.source);
      if (kind !== 'rdbm' && kind !== 'servers') return false;
      // DynamoDB resources are region-addressed and carry no port at all (source
      // "dynamodb", no portExposures) — the port gate would silently drop their cards.
      const isDynamo = String(r.engine || r.source || '').trim().toLowerCase() === 'dynamodb';
      const hasPort =
        (Array.isArray(r.portExposures) && r.portExposures.length > 0) || r.port != null;
      if (!hasPort && !isDynamo) return false;
      return !isResourceFullyConnected(r, connections);
    });
  }, [landscapeSources, connections]);

  // Last-test verdicts joined from the globally-loaded connections (lastTestSuccess is the
  // tri-state persisted by the backend's save automation): id → true/false/undefined.
  const testResultById = useMemo(() => {
    const map = {};
    (connections || []).forEach((c) => {
      if (c && c.id != null) map[String(c.id)] = c.lastTestSuccess === true;
    });
    return map;
  }, [connections]);

  // The engines a resource card implies: the resource's own engine (managed DBs) or the
  // engines its classified ports matched. True (button = Connect) when ANY implied engine
  // has a driver, or when nothing is implied — the per-port page/editor handles the rest.
  const resourceHasDriver = (resource) => {
    const engines = resource.engine
      ? [resource.engine]
      : portChipsFor(resource).map((p) => p.engine).filter(Boolean);
    if (!engines.length) return true;
    return engines.some((engine) => engineHasDriver(engine, drivers));
  };

  // "Connect" — a single-port resource opens the Source Editor pre-filled (mirrors the
  // Undiscovered Sources page); a multi-port resource goes to the per-port table page so
  // the user picks which port to connect. encodeURIComponent so base64 '+'/'/' survive
  // URLSearchParams (and slash-bearing cloud identifiers survive path matching).
  const createConnection = (resource) => {
    const allPorts = portChipsFor(resource);
    const ports = hideInternal ? allPorts.filter((p) => !p.internal) : allPorts;
    if (ports.length > 1) {
      history.push({
        pathname: `/landscape/ports/${encodeURIComponent(resource.identifier || '')}`,
        state: { resource },
      });
      return;
    }
    const single = ports[0];
    const overrides = single
      ? { port: parseInt(single.label, 10) || undefined, engine: single.engine || undefined }
      : {};
    const prefill = encodePrefill(managerSourceToEditorPrefill(resource, overrides));
    history.push(`/sources/source-editor?prefill=${encodeURIComponent(prefill)}`);
  };

  // The app-wide Notification toast renders `header`/`body` — passing `variant`/`message`
  // produced the empty toast the pipeline actions were popping.
  const notify = (header, body) => {
    if (setNotification) setNotification({ header, body });
  };

  const onAction = async (source, stage) => {
    const id = getDataSourceNumericId({ id: source.id, tableData: { id: source.id } }) ?? Number(source.id);
    if (stage.key === 'discovered' && prodIds.has(String(source.id))) {
      history.push(`/enterprise/source/${encodeURIComponent(id)}/findings`);
      return;
    }
    if (stage.key === 'discovered' || stage.key === 'masked') {
      history.push(`/secure/mask/inventory/${encodeURIComponent(id)}`);
      return;
    }
    setBusyId(source.id);
    try {
      const gate = await ensureConnectionTested(id, {
        history,
        setNotification: (n) => notify(n.header, n.body),
        name: source.name,
      });
      if (!gate.ok) {
        setBusyId(null);
        return;
      }
      await startDiscoveryWithRule(id);
      notify('Discovery started', `Discovery is now running for ${source.name}.`);
      // Optimistically keep the card in "Running…" (and pinned to the top of its column).
      setStartedAt((prev) => ({ ...prev, [source.id]: Date.now() }));
    } catch (e) {
      console.log(`Pipeline action failed for ${source.name}`, e);
      notify(`Could not start job for ${source.name}`, String(e.message || e));
    }
    setBusyId(null);
  };

  // A source is "running" if a live job was seen in the job lists OR we just started one here
  // (within the optimistic TTL, after which the polled runningById is the sole source of truth).
  const isRunning = (source) => {
    if ((runningById || {})[source.id]) return true;
    const ts = startedAt[source.id];
    return ts != null && Date.now() - ts < STARTED_TTL_MS;
  };

  // Filter by connection name / engine / database before bucketing into columns.
  const q = query.trim().toLowerCase();
  const matchesQuery = (source) =>
    !q ||
    [source.name, source.engine, source.datasourceType, source.database]
      .some((f) => String(f || '').toLowerCase().includes(q));

  // Saved sources whose test failed/never ran land in `savedSurfaced` and render in the
  // Surfaced column below the cloud-landscape candidates.
  const grouped = { scanned: [], discovered: [], masked: [] };
  const savedSurfaced = [];
  // The same census as the page header (idempotent when Home already applied it): no
  // landscape=none rows, no platform-managed rows, no sub-sources — a large "Discover
  // everything" fan-out would otherwise flood every column.
  buildCensus(sources || [], connections).visible
    .filter(matchesQuery)
    .forEach((source) => {
    const stage = stageOf(source, { maskStatusById: maskStatusById || {}, profiledIds, testResultById });
    if (stage === 'surfaced') {
      savedSurfaced.push(source);
    } else {
      grouped[stage].push(source);
    }
  });
  // Running sources bubble to the top of their column; the rest sort by most recent
  // discovery first (sources with no discovery yet fall to the bottom of the column).
  const latestTs = (s) => (s.latestDiscoveryAt ? Date.parse(s.latestDiscoveryAt) || 0 : 0);
  Object.keys(grouped).forEach((key) => {
    grouped[key].sort((a, b) => {
      const runDelta = (isRunning(b) ? 1 : 0) - (isRunning(a) ? 1 : 0);
      if (runDelta !== 0) return runDelta;
      return latestTs(b) - latestTs(a);
    });
  });
  const matchCount =
    Object.values(grouped).reduce((n, list) => n + list.length, 0) + savedSurfaced.length;

  return (
    <div className='mb-3'>
      <div className='d-flex align-items-center justify-content-between mb-2' style={{ gap: 12 }}>
        <h2 className='text-left sg-eyebrow mb-0'>Pipeline</h2>
        <div className='d-flex align-items-center' style={{ gap: 8 }}>
          <input
            type='text'
            data-testid='pipeline-search'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Filter by name or engine…'
            style={{
              fontSize: 12.5,
              padding: '5px 10px',
              borderRadius: 'var(--r-sm)',
              border: '1px solid var(--ink-150)',
              background: 'var(--surface)',
              minWidth: 220,
            }}
          />
          {q && (
            <span className='sg-small' style={{ color: 'var(--ink-500)' }}>
              {matchCount} match{matchCount === 1 ? '' : 'es'}
            </span>
          )}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        {STAGES.map((stage) => {
          const isSurfaced = stage.key === 'surfaced';
          const items = isSurfaced ? surfaced : grouped[stage.key];
          const count = isSurfaced ? surfaced.length + savedSurfaced.length : items.length;
          return (
            <div
              key={stage.key}
              style={{ background: 'var(--canvas)', borderRadius: 'var(--r-md)', padding: 10, minHeight: 240 }}
              data-testid={`pipeline-col-${stage.key}`}
            >
              <div className='d-flex align-items-center mb-2' style={{ gap: 7 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: stage.dot, flex: 'none' }} />
                <span className='sg-eyebrow' style={{ fontSize: 11 }}>
                  {stage.title}
                </span>
                <span className='sg-count-chip' style={{ background: 'var(--surface)' }}>
                  {count}
                </span>
              </div>
              {isSurfaced
                ? (
                  <>
                    {items.map((resource) => (
                      <SurfacedCard
                        key={resource.identifier || `${resource.source}-${ipDnsForResource(resource)}-${resource.port}`}
                        resource={resource}
                        onCreate={createConnection}
                        hideInternal={hideInternal}
                        hasDriver={resourceHasDriver(resource)}
                        onUploadDriver={() => history.push('/drivers')}
                      />
                    ))}
                    {savedSurfaced.map((source) => (
                      <SavedSourceCard
                        key={source.id}
                        source={{
                          ...source,
                          lastTestSuccess: (connections || []).find((c) => String(c.id) === String(source.id))?.lastTestSuccess ?? null,
                        }}
                        onRetried={loadConnectionsHandler}
                        notify={notify}
                      />
                    ))}
                  </>
                )
                : items.map((source) => (
                    <PipelineCard
                      key={source.id}
                      source={source}
                      provenance={
                        // Pipeline sources come off the risk overview, which carries no origin —
                        // join provenance from the globally-loaded connections.
                        (connections || []).find((c) => String(c.id) === String(source.id))
                      }
                      stage={stage}
                      maskStatus={(maskStatusById || {})[source.id]}
                      onAction={onAction}
                      busy={busyId === source.id}
                      running={isRunning(source)}
                      profiled={profiledIds.has(Number(source.id))}
                      locked={stage.key === 'masked' && prodIds.has(String(source.id))}
                      handoff={stage.key === 'discovered' && prodIds.has(String(source.id))}
                      // Discovered / Secured only open the inventory or findings — reviewable.
                      readOnlyUser={!canEdit && stage.key !== 'discovered' && stage.key !== 'masked'}
                      subCount={subCountByParent.get(String(source.id)) || 0}
                    />
                  ))}
              {count === 0 && (
                <div className='sg-small text-center' style={{ color: 'var(--ink-500)', paddingTop: 30 }}>
                  {isSurfaced ? 'Nothing surfaced' : 'No sources'}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PipelineBoard;
export { stageOf };
